import React, { useState, type FunctionComponent } from "react";
import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, message, Popconfirm, Space, Switch, Table, Tooltip } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { discountActions, RequestStatus, type AppStore, type ProductWithStats } from "@fitness-app/app-store";
import {
  DiscountAction,
  DiscountDurationScope,
  DiscountStatus,
  DiscountStripeSyncStatus,
  DiscountType,
  type CreateDiscountDTO,
  type DiscountDocument,
  type UpdateDiscountDTO,
} from "@fitness-app/data-models/entities/Discount";
import { type ProductPrice } from "@fitness-app/data-models/entities/Product";

import ModalForm from "~/components/ModalForm/ModalForm";
import { useLoading } from "~/hooks/useLoading";
import DiscountForm from "~/modules/Products/Discounts/DiscountForm/DiscountForm";
import { type DiscountFormModel } from "~/modules/Products/Discounts/DiscountForm/types";
import DiscountGatewayCoverage from "~/modules/Products/Discounts/DiscountGatewayCoverage";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

const getPrices = (product: ProductWithStats): ProductPrice[] =>
  "prices" in product && Array.isArray(product.prices) ? product.prices : [];

const currencyLabel = (currency: DiscountDocument["currency"]): string => {
  if (!currency) return "";
  return currency.toLowerCase() === "pln" ? "zł" : currency.toUpperCase();
};

const formatDiscountValue = (value: number, row: DiscountDocument): string => {
  if (row.type === DiscountType.Percent) {
    return `${value}%`;
  }
  const label = currencyLabel(row.currency);
  return label ? `${value} ${label}` : `${value}`;
};

const DiscountsList: FunctionComponent = () => {
  const { t } = useTranslation(["settings", "common"]);
  const [loading, startLoading, stopLoading] = useLoading();
  const dispatch = useAppDispatch();
  const [showDiscountForm, toggleDiscountForm] = useState(false);
  const [discountModel, setDiscountModel] = useState<DiscountFormModel | null>(null);
  const [remoteTakenCodes, setRemoteTakenCodes] = useState<string[]>([]);

  const { discounts, discountsListStatus, discountAction } = useAppSelector((store: AppStore) => store.discount);
  const productsList = useAppSelector((store) => store.products.productsListData);

  const refetchDiscounts = () => {
    void dispatch(discountActions.fetchDiscounts());
  };

  const findProductIdByPrice = (priceId: string) => {
    const productId = productsList?.find((product) => getPrices(product).some((price) => price.id === priceId))?.id;

    if (!productId) throw new Error(`Product ${productId} not exist`);

    return productId;
  };

  const onAddNewDiscount = (data: DiscountFormModel) => {
    startLoading();

    const discountDto: CreateDiscountDTO = {
      code: data.code,
      type: data.type,
      status: data.status ?? DiscountStatus.Active,
      value: data.value,
      active: data.active,
      products: data.products.map((id) => ({ id })),
      prices: data.prices ? data.prices.map((id) => ({ id, productId: findProductIdByPrice(id) })) : [],
      quantityLimit: data.showQuantityLimit ? data.quantityLimit : null,
      limitRange: data.showTimeLimit
        ? {
            from: data.limitRange?.from ? new Date(data.limitRange.from.valueOf()) : new Date(),
            to: data.limitRange?.to ? new Date(data.limitRange.to.valueOf()) : null,
          }
        : null,
      currency: data.currency,
      durationScope: data.durationScope ?? DiscountDurationScope.Once,
    };
    void dispatch(
      discountActions.addDiscount({
        payload: discountDto,
        onSuccess: () => {
          stopLoading();
          toggleDiscountForm(false);
          refetchDiscounts();
        },
        onFailure: (errorCode) => {
          stopLoading();
          if (errorCode === 409) {
            setRemoteTakenCodes((prev) => (prev.includes(discountDto.code) ? prev : [...prev, discountDto.code]));
            return;
          }
          void message.error(t("settings:discount.errorWhenUpdatingDiscount"));
        },
      }),
    );
  };

  const onEditDiscount = (data: DiscountFormModel) => {
    startLoading();

    const updateDiscountDTO: UpdateDiscountDTO = {
      ...data,
      id: String(discountModel?.id),
      products: data.products.map((id) => ({ id })),
      prices: data.prices ? data.prices.map((id) => ({ id, productId: findProductIdByPrice(id) })) : [],
      // Same normalisation as when adding a discount: the quantity field is
      // unmounted while the limit is off, so spreading the raw form model can
      // drop the key entirely and the DTO validator rejects the whole update.
      quantityLimit: data.showQuantityLimit ? (data.quantityLimit ?? null) : null,
      limitRange: data.showTimeLimit
        ? {
            from: data.limitRange?.from ? new Date(data.limitRange.from.valueOf()) : new Date(),
            to: data.limitRange?.to ? new Date(data.limitRange.to.valueOf()) : null,
          }
        : null,
      action: DiscountAction.Update,
      status: data.status ?? DiscountStatus.Active,
      durationScope: data.durationScope ?? DiscountDurationScope.Once,
    };
    void dispatch(
      discountActions.updateDiscount({
        payload: updateDiscountDTO,
        onSuccess: () => {
          stopLoading();
          setDiscountModel(null);
          toggleDiscountForm(false);
          refetchDiscounts();
        },
        onFailure: () => {
          stopLoading();
          // Without this the form just sits there on a rejected update (e.g. a
          // DTO validation error) and looks like nothing happened.
          void message.error(t("settings:discount.errorWhenUpdatingDiscount"));
        },
      }),
    );
  };

  const handleEdit = (row: DiscountDocument) => {
    setDiscountModel({
      id: row.id,
      code: row.code,
      status: row.status,
      type: row.type,
      prices: row.prices?.map((price) => price.id) || [],
      quantityLimit: row.quantityLimit,
      active: row.active,
      currency: row.currency,
      limitRange: row.limitRange
        ? {
            from: dayjs(row.limitRange.from),
            to: row.limitRange.to ? dayjs(row.limitRange.to) : null,
          }
        : null,
      value: row.value,
      products: row.products.map((product) => product.id),
      showTimeLimit: Boolean(row.limitRange),
      showQuantityLimit: Boolean((row.quantityLimit ?? 0) > 0),
      durationScope: row.durationScope ?? DiscountDurationScope.Once,
    });
    toggleDiscountForm(true);
  };

  return (
    <div>
      <Card
        bordered={false}
        extra={
          <Button type="primary" onClick={() => toggleDiscountForm(true)} icon={<PlusOutlined />}>
            {t("common:button.addNewDiscount")}
          </Button>
        }
      >
        <Table<DiscountDocument>
          dataSource={discounts ?? []}
          rowKey="id"
          size="middle"
          scroll={{ x: true }}
          loading={discountsListStatus === RequestStatus.FETCHING || discountsListStatus === RequestStatus.UPDATING}
          pagination={false}
        >
          <Table.Column<DiscountDocument> title={t("settings:discount.list.column.code")} dataIndex="code" key="code" />
          <Table.Column<DiscountDocument>
            title={t("settings:discount.list.column.value")}
            dataIndex="value"
            key="value"
            render={(value: number, row) => formatDiscountValue(value, row)}
          />
          <Table.Column<DiscountDocument>
            title={t("settings:discount.list.column.gateways")}
            key="gateways"
            render={(_, row) => (
              <DiscountGatewayCoverage
                productIds={row.products.map((p) => p.id)}
                products={productsList ?? []}
                isStripeSynced={row.stripe?.syncStatus === DiscountStripeSyncStatus.Synced}
                stripeSyncError={
                  row.stripe?.syncStatus === DiscountStripeSyncStatus.Failed ? row.stripe?.syncError : null
                }
                stripeSyncErrorCode={
                  row.stripe?.syncStatus === DiscountStripeSyncStatus.Failed ? row.stripe?.syncErrorCode : null
                }
                stripeSyncErrorParams={
                  row.stripe?.syncStatus === DiscountStripeSyncStatus.Failed ? row.stripe?.syncErrorParams : null
                }
              />
            )}
          />
          <Table.Column<DiscountDocument>
            title={t("settings:discount.list.column.createdAt")}
            dataIndex="createdAt"
            key="createdAt"
            render={(createdAt: Date) => dayjs(createdAt).format("DD.MM.YYYY HH:mm")}
          />
          <Table.Column<DiscountDocument>
            title={t("settings:discount.list.column.used")}
            dataIndex="used"
            key="used"
            render={(used: number, row) =>
              `${used + (row.pendingOrders?.reduce((sum, order) => sum + (order?.quantity ?? 0), 0) ?? 0)}`
            }
          />
          <Table.Column<DiscountDocument>
            title={
              <div>
                {t("settings:discount.list.column.active")}{" "}
                <Tooltip title={t("settings:discount.list.column.active")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
            }
            dataIndex="active"
            key="active"
            render={(active: boolean, row) => {
              const currentlyProcessed = discountAction ? discountAction : null;

              return (
                <Switch
                  checked={active}
                  onChange={() =>
                    void dispatch(
                      discountActions.updateDiscount({
                        onSuccess: () => refetchDiscounts(),
                        onFailure: () => {
                          void message.error(t("settings:discount.errorWhenUpdatingDiscount"));
                        },
                        payload: {
                          active: !active,
                          id: row.id,
                          action: DiscountAction.ToggleVisibility,
                        },
                      }),
                    )
                  }
                  loading={
                    currentlyProcessed
                      ? currentlyProcessed.id === row.id &&
                        currentlyProcessed.action === DiscountAction.ToggleVisibility
                      : false
                  }
                />
              );
            }}
          />
          <Table.Column<DiscountDocument>
            title={t("settings:discount.list.column.actions")}
            dataIndex="actions"
            key="action"
            render={(_, row) => {
              const currentlyProcessed = discountAction ? discountAction : null;

              return (
                <Space>
                  <Button type="link" onClick={() => handleEdit(row)} key={`edit-link-${row.id}`}>
                    {t("common:button.edit")}
                  </Button>
                  {row.used ? (
                    <Popconfirm
                      overlayStyle={{ maxWidth: 400 }}
                      placement="leftTop"
                      title={t("settings:discount.archiveDiscountWarning")}
                      onConfirm={() =>
                        void dispatch(
                          discountActions.updateDiscount({
                            onSuccess: () => refetchDiscounts(),
                            onFailure: () => {
                              void message.error(t("settings:discount.errorWhenUpdatingDiscount"));
                            },
                            payload: {
                              action: DiscountAction.Archive,
                              id: row.id,
                            },
                          }),
                        )
                      }
                    >
                      <Button
                        type="link"
                        danger
                        loading={
                          currentlyProcessed
                            ? currentlyProcessed.id === row.id && currentlyProcessed.action === DiscountAction.Archive
                            : false
                        }
                      >
                        {t("common:button.archive")}
                      </Button>
                    </Popconfirm>
                  ) : (
                    <Popconfirm
                      overlayStyle={{ maxWidth: 400 }}
                      placement="leftTop"
                      title={t("settings:discount.deleteDiscountWarning")}
                      onConfirm={() =>
                        void dispatch(
                          discountActions.updateDiscount({
                            onSuccess: () => refetchDiscounts(),
                            onFailure: () => {
                              void message.error(t("settings:discount.errorWhenUpdatingDiscount"));
                            },
                            payload: {
                              action: DiscountAction.Remove,
                              id: row.id,
                            },
                          }),
                        )
                      }
                    >
                      <Button
                        type="link"
                        danger
                        loading={
                          currentlyProcessed
                            ? currentlyProcessed.id === row.id && currentlyProcessed.action === DiscountAction.Remove
                            : false
                        }
                      >
                        {t("common:button.delete")}
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              );
            }}
          />
        </Table>
        <ModalForm<DiscountFormModel>
          open={showDiscountForm}
          onCancel={() => {
            toggleDiscountForm(false);
            setDiscountModel(null);
          }}
          title={discountModel?.id ? t("settings:discount.edit") : t("settings:discount.addNew")}
          width={900}
          okButtonProps={{
            disabled: loading,
          }}
          loading={loading}
          okText={t("common:button.save")}
        >
          <DiscountForm
            onSubmit={discountModel ? onEditDiscount : onAddNewDiscount}
            model={discountModel}
            extraTakenCodes={remoteTakenCodes}
          />
        </ModalForm>
      </Card>
    </div>
  );
};

export default DiscountsList;
